/**
 * Eagerly start the chunk downloads for every settings sub-page so the
 * first sidebar click after the shell mounts never hits a Suspense
 * fallback. Deferred via `setTimeout` so we don't race the requested
 * page's own chunk through the HTTP/2 stream pool, and latched via a
 * module-level flag so re-entry into settings within the same session
 * doesn't re-fire the work.
 *
 * `setTimeout` is used instead of `requestIdleCallback` because the
 * app-wide polyfill in `config.ts` overrides `requestIdleCallback` with
 * a synchronous shim (`cb => cb()`) on browsers that lack it — that
 * defeats the defer in Safari. The small fixed timeout is sufficient
 * to yield to the requested page's chunk.
 *
 * Add an entry to `LAZY_PAGE_IMPORTERS` for every new `/settings/*`
 * sub-route — drift is invisible until the first user clicks the new
 * sidebar item.
 */
const LAZY_PAGE_IMPORTERS: Array<() => Promise<unknown>> = [
  () => import('Pages/Settings/General/MainInfoPreferencesPageV3'),
  () => import('Pages/Settings/General/ThemePage/ThemePageV3'),
  () => import('Pages/Settings/General/AIPreferencesPageV3'),
  () => import('Pages/Settings/Auth/PasswordManagementPageV3'),
  () => import('Pages/Settings/Auth/IdentityAndAccessPageV3'),
  () => import('Pages/Settings/DataLake/SnowflakeConfigurationPageV3'),
  () => import('Pages/Settings/DataLake/ScheduledSearchesPageV3'),
  () => import('Pages/Settings/Developer/GitSyncPageV3'),
  () => import('Pages/Settings/Developer/PantherAnalysisToolPageV3'),
  () => import('Pages/Settings/AIAutomation/ConfigurationPageV3'),
  () => import('Pages/Settings/AIAutomation/WebAccessPageV3'),
  () => import('Pages/Settings/AIAutomation/RemoteMcpPageV3'),
  () => import('Pages/Settings/AIAutomation/SkillsPageV3'),
  () => import('Pages/Settings/AIAutomation/McpServersPageV3'),
  () => import('Pages/Settings/Alerts/ContextTags/ContextTagsPageV3'),
  () => import('Pages/Settings/Usage'),
  () => import('Pages/Settings/UserRoles/ListRoles/ListRolesV3'),
  () => import('Pages/Settings/Users/ListUsersV3'),
  () => import('Pages/Settings/APIToken'),
];

let preloadFired = false;

export const preloadSettingsChunks = (): void => {
  if (preloadFired) {
    return;
  }
  preloadFired = true;

  window.setTimeout(() => {
    Promise.all(LAZY_PAGE_IMPORTERS.map(importer => importer())).catch(() => {
      // Reset the latch so a follow-up mount can retry; navigation paths
      // still own the real lazy() resolution + ErrorBoundary surfacing.
      preloadFired = false;
    });
  }, 200);
};
